import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Opportunity,
  Requirement,
  DraftSection,
  UserProfile,
  LibraryAnswerMatch,
  SmeTask,
} from "../../types";
import { subscribeToRequirements } from "../../services/requirementsService";
import {
  subscribeToDraftSections,
  saveDraftSection,
  deleteDraftSection,
  generateAiDraftSection,
  matchLibraryAnswers,
  countWords,
  countPlaceholders,
} from "../../services/draftsService";
import { buildProposalSectionGroups } from "../../services/teamAllocationService";
import { subscribeToOpportunitySmeTasks } from "../../services/smeTasksService";
import { ExpandableCard } from "../common/ExpandableCard";
import { AskSmeModal } from "./AskSmeModal";
import { MarkdownEditorPreview } from "./drafting/MarkdownEditorPreview";
import { BatchAutoDraftModal, BatchAutoDraftCandidate } from "./drafting/BatchAutoDraftModal";
import {
  PenTool,
  Sparkles,
  Search,
  AlertCircle,
  Save,
  Trash2,
  BookOpen,
  ArrowRight,
  Loader2,
  RefreshCw,
  Layers,
  List,
  User,
  HelpCircle,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";

interface DraftingTabProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  stagedEdit?: { requirementId: string; text: string } | null;
  onClearStagedEdit?: () => void;
  onSwitchTab?: (tab: string) => void;
  onOpenHarvest?: () => void;
}

type ViewMode = "sections" | "flat";
type SpecialFilter = "all" | "mine" | "gaps" | "actions";

export const DraftingTab: React.FC<DraftingTabProps> = ({
  opportunity,
  user,
  stagedEdit,
  onClearStagedEdit,
  onOpenHarvest,
}) => {
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [draftSections, setDraftSections] = useState<DraftSection[]>([]);
  const [smeTasks, setSmeTasks] = useState<SmeTask[]>([]);

  // Navigation & View States
  const [viewMode, setViewMode] = useState<ViewMode>("sections");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [specialFilter, setSpecialFilter] = useState<SpecialFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isMatchingAll, setIsMatchingAll] = useState(false);
  const [matchSuccessMsg, setMatchSuccessMsg] = useState<string | null>(null);

  // Expanded sections state for "sections" view mode
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Active requirement draft edits in local state
  const [draftEdits, setDraftEdits] = useState<
    Record<
      string,
      {
        content: string;
        status: "draft" | "review" | "approved";
        usedAnswerIds?: string[];
        isSaving?: boolean;
      }
    >
  >({});

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmDeleteReq, setConfirmDeleteReq] = useState<Requirement | null>(null);

  // Active AI generation states per requirementId
  const [isGenerating, setIsGenerating] = useState<Record<string, boolean>>({});
  const [confirmOverwriteReqId, setConfirmOverwriteReqId] = useState<string | null>(null);

  // Card expansion and inline messaging states
  const [expandedCardIds, setExpandedCardIds] = useState<Record<string, boolean>>({});
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [cardSuccess, setCardSuccess] = useState<Record<string, string>>({});

  // Matched answers cache per requirementId
  const [requirementMatches, setRequirementMatches] = useState<
    Record<string, LibraryAnswerMatch[]>
  >({});
  const [loadingMatches, setLoadingMatches] = useState<Record<string, boolean>>({});

  // SME Workflow states
  const [askSmeReq, setAskSmeReq] = useState<Requirement | null>(null);

  // Batch Auto-Draft states
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isBatchAutoDrafting, setIsBatchAutoDrafting] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(
    null,
  );

  // 1. Subscribe to requirements
  useEffect(() => {
    const unsub = subscribeToRequirements(opportunity.id, setRequirements);
    return () => unsub();
  }, [opportunity.id]);

  // 2. Subscribe to draft sections
  useEffect(() => {
    const unsub = subscribeToDraftSections(opportunity.id, setDraftSections);
    return () => unsub();
  }, [opportunity.id]);

  // 3. Subscribe to opportunity SME tasks
  useEffect(() => {
    const unsub = subscribeToOpportunitySmeTasks(opportunity.id, setSmeTasks);
    return () => unsub();
  }, [opportunity.id]);

  // 4. Handle staged edit from Writing Coach ("Apply Suggestion")
  const [prevStagedEdit, setPrevStagedEdit] = useState<{
    requirementId: string;
    text: string;
  } | null>(null);
  if (stagedEdit && stagedEdit !== prevStagedEdit) {
    setPrevStagedEdit(stagedEdit);
    setDraftEdits((prev) => ({
      ...prev,
      [stagedEdit.requirementId]: {
        content: stagedEdit.text,
        status: prev[stagedEdit.requirementId]?.status || "draft",
      },
    }));
  }

  useEffect(() => {
    if (stagedEdit && onClearStagedEdit) {
      onClearStagedEdit();
    }
  }, [stagedEdit, onClearStagedEdit]);

  // Lookup map: requirementId -> existing DraftSection
  const sectionMap = useMemo(() => {
    const map = new Map<string, DraftSection>();
    draftSections.forEach((s) => map.set(s.requirementId, s));
    return map;
  }, [draftSections]);

  // Build Stage 3 proposal section groups
  const sectionGroups = useMemo(() => {
    return buildProposalSectionGroups(draftSections, requirements);
  }, [draftSections, requirements]);

  // Filtered requirements
  const filteredRequirements = useMemo(() => {
    return requirements.filter((req) => {
      const section = sectionMap.get(req.id);
      const currentContent = draftEdits[req.id]?.content ?? section?.content ?? "";
      const status = section ? section.status : "unanswered";

      // 1. Status filter
      if (statusFilter !== "all" && status !== statusFilter) {
        return false;
      }

      // 2. Special filter
      if (specialFilter === "mine") {
        const userEmailLower = user?.email?.toLowerCase();
        if (!userEmailLower) return false;
        const matchesAssignee =
          req.assignedToEmail?.toLowerCase() === userEmailLower ||
          section?.assignedToEmail?.toLowerCase() === userEmailLower;
        if (!matchesAssignee) return false;
      } else if (specialFilter === "gaps") {
        const matches = requirementMatches[req.id];
        const isUnanswered = !currentContent.trim() || status === "unanswered";
        const hasNoMatches = matches && matches.length === 0;
        if (!isUnanswered && !hasNoMatches) return false;
      } else if (specialFilter === "actions") {
        const placeholders = countPlaceholders(currentContent);
        if (placeholders === 0) return false;
      }

      // 3. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = req.title.toLowerCase().includes(q);
        const matchesPrompt = req.prompt.toLowerCase().includes(q);
        const matchesContent = currentContent.toLowerCase().includes(q);
        const matchesSectionRef = req.sectionRef?.toLowerCase().includes(q) || false;
        return matchesTitle || matchesPrompt || matchesContent || matchesSectionRef;
      }

      return true;
    });
  }, [
    requirements,
    sectionMap,
    draftEdits,
    statusFilter,
    specialFilter,
    searchQuery,
    user?.email,
    requirementMatches,
  ]);

  // Progress metrics
  const totalSections = requirements.length;
  const approvedCount = draftSections.filter((s) => s.status === "approved").length;
  const inReviewCount = draftSections.filter((s) => s.status === "review").length;
  const draftedCount = draftSections.filter((s) => s.status === "draft").length;
  const totalWords = draftSections.reduce((sum, s) => sum + s.wordCount, 0);

  // Calculate target word count planned in Stage 3
  const totalTargetWords = useMemo(() => {
    return sectionGroups.reduce((sum, g) => sum + (g.targetWordCount || 0), 0);
  }, [sectionGroups]);

  // Identify candidates for Batch Auto-Draft (unanswered with top match >= 75%)
  const eligibleBatchCandidates = useMemo((): BatchAutoDraftCandidate[] => {
    const list: BatchAutoDraftCandidate[] = [];
    requirements.forEach((req) => {
      const section = sectionMap.get(req.id);
      const currentContent = draftEdits[req.id]?.content ?? section?.content ?? "";
      if (currentContent.trim()) return; // Already has content

      const matches = requirementMatches[req.id] || [];
      const topMatch = matches[0];
      if (topMatch && topMatch.matchScore >= 0.75) {
        list.push({ requirement: req, topMatch });
      }
    });
    return list;
  }, [requirements, sectionMap, draftEdits, requirementMatches]);

  // Match all requirements with Answer Library
  const handleMatchAll = async () => {
    setIsMatchingAll(true);
    setMatchSuccessMsg(null);
    try {
      const res = await matchLibraryAnswers({ opportunityId: opportunity.id });
      setMatchSuccessMsg(
        `Successfully matched ${res.totalRequirementsMatched} requirement(s) against ${res.totalAnswersEvaluated} Answer Library records.`,
      );

      // Populate local match cache
      const newMatches: Record<string, LibraryAnswerMatch[]> = {};
      res.results.forEach((r) => {
        newMatches[r.requirementId] = r.topMatches;
      });
      setRequirementMatches((prev) => ({ ...prev, ...newMatches }));
    } catch (err) {
      console.error("Match error:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to match requirements with Answer Library.",
      );
    } finally {
      setIsMatchingAll(false);
    }
  };

  // Fetch matches for a single requirement if drawer is opened
  const handleLoadMatchesForRequirement = async (reqId: string) => {
    if (requirementMatches[reqId] || loadingMatches[reqId]) return;
    setLoadingMatches((prev) => ({ ...prev, [reqId]: true }));
    try {
      const res = await matchLibraryAnswers({
        opportunityId: opportunity.id,
        requirementId: reqId,
      });
      if (res.results[0]) {
        setRequirementMatches((prev) => ({
          ...prev,
          [reqId]: res.results[0]?.topMatches || [],
        }));
      }
    } catch (err) {
      console.warn("Could not load matches for requirement:", err);
    } finally {
      setLoadingMatches((prev) => ({ ...prev, [reqId]: false }));
    }
  };

  // Save draft section to Firestore
  const handleSaveDraft = async (req: Requirement) => {
    const existing = sectionMap.get(req.id);
    const currentEdit = draftEdits[req.id] || {
      content: existing?.content || "",
      status: existing?.status || "draft",
      usedAnswerIds: existing?.usedAnswerIds || [],
    };

    setDraftEdits((prev) => ({
      ...prev,
      [req.id]: { ...currentEdit, isSaving: true },
    }));

    const reqTargetWords =
      req.targetWordCount ||
      Math.round(
        (sectionGroups.find((g) => g.requirements.some((r) => r.id === req.id))?.targetWordCount ||
          500) /
          Math.max(
            1,
            sectionGroups.find((g) => g.requirements.some((r) => r.id === req.id))?.requirements
              .length || 1,
          ),
      );

    try {
      await saveDraftSection(
        opportunity.id,
        {
          id: existing?.id,
          requirementId: req.id,
          title: req.title,
          content: currentEdit.content,
          status: currentEdit.status,
          matchedAnswerIds: existing?.matchedAnswerIds || [],
          usedAnswerIds: currentEdit.usedAnswerIds || existing?.usedAnswerIds || [],
          humanEdited: true,
          sectionRef: req.sectionRef,
          category: req.category,
          assignedTo: req.assignedTo,
          assignedToEmail: req.assignedToEmail,
          targetWordCount: reqTargetWords,
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );
    } catch (err) {
      console.error("Failed to save draft:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to save draft section.");
    } finally {
      setDraftEdits((prev) => ({
        ...prev,
        [req.id]: { ...currentEdit, isSaving: false },
      }));
    }
  };

  // Trigger delete confirmation
  const handleDeleteDraft = (req: Requirement) => {
    const existing = sectionMap.get(req.id);
    if (!existing) return;
    setConfirmDeleteReq(req);
  };

  // Execute deletion
  const executeDeleteDraft = async (req: Requirement) => {
    const existing = sectionMap.get(req.id);
    if (!existing) return;
    setConfirmDeleteReq(null);

    try {
      await deleteDraftSection(opportunity.id, existing.id);
      setDraftEdits((prev) => {
        const next = { ...prev };
        delete next[req.id];
        return next;
      });
    } catch (err) {
      console.error("Failed to delete draft section:", err);
      setErrorMessage(err instanceof Error ? err.message : "Failed to delete draft section.");
    }
  };

  // Run AI Synthesis
  const handleRunAiSynthesis = async (req: Requirement, force = false) => {
    // Automatically expand the card so user immediately sees live synthesis and editor
    setExpandedCardIds((prev) => ({ ...prev, [req.id]: true }));

    const existing = sectionMap.get(req.id);
    const currentEdit = draftEdits[req.id];
    const hasExistingContent = Boolean(currentEdit?.content?.trim() || existing?.content?.trim());

    if (hasExistingContent && !force) {
      setConfirmOverwriteReqId(req.id);
      return;
    }

    setConfirmOverwriteReqId(null);
    setIsGenerating((prev) => ({ ...prev, [req.id]: true }));
    setCardErrors((prev) => {
      const next = { ...prev };
      delete next[req.id];
      return next;
    });

    try {
      const cachedMatches = requirementMatches[req.id];
      let resolvedMatches: LibraryAnswerMatch[] = cachedMatches ? [...cachedMatches] : [];

      if (resolvedMatches.length === 0) {
        try {
          const matchRes = await matchLibraryAnswers({
            opportunityId: opportunity.id,
            requirementId: req.id,
          });
          const fetchedMatches = matchRes?.results?.[0]?.topMatches;
          if (fetchedMatches && fetchedMatches.length > 0) {
            resolvedMatches = [...fetchedMatches];
            setRequirementMatches((prev) => ({ ...prev, [req.id]: fetchedMatches }));
          }
        } catch (matchErr) {
          console.warn("Could not auto-fetch matches for requirement:", matchErr);
        }
      }

      const result = await generateAiDraftSection({
        opportunityId: opportunity.id,
        requirementId: req.id,
        requirementTitle: req.title,
        requirementPrompt: req.prompt,
        requirementCategory: req.category,
        clientName: opportunity.client,
        opportunityTitle: opportunity.title,
        matchedAnswers: resolvedMatches,
      });

      const returnedUsed = result.answersUsed || resolvedMatches.map((m) => m.answerId);

      // Update local edit
      setDraftEdits((prev) => ({
        ...prev,
        [req.id]: {
          content: result.text,
          status: "draft",
          usedAnswerIds: returnedUsed,
        },
      }));

      // Automatically persist synthesized draft to Firestore
      const reqTargetWords =
        req.targetWordCount ||
        Math.round(
          (sectionGroups.find((g) => g.requirements.some((r) => r.id === req.id))
            ?.targetWordCount || 500) /
            Math.max(
              1,
              sectionGroups.find((g) => g.requirements.some((r) => r.id === req.id))?.requirements
                .length || 1,
            ),
        );

      await saveDraftSection(
        opportunity.id,
        {
          id: existing?.id,
          requirementId: req.id,
          title: req.title,
          content: result.text,
          status: "draft",
          matchedAnswerIds: resolvedMatches.map((m) => m.answerId),
          usedAnswerIds: returnedUsed,
          humanEdited: false,
          sectionRef: req.sectionRef,
          category: req.category,
          assignedTo: req.assignedTo,
          assignedToEmail: req.assignedToEmail,
          targetWordCount: reqTargetWords,
        },
        user ? { displayName: user.displayName, email: user.email } : undefined,
      );

      setCardSuccess((prev) => ({ ...prev, [req.id]: "Narrative synthesized and saved!" }));
      setTimeout(() => {
        setCardSuccess((prev) => {
          const next = { ...prev };
          delete next[req.id];
          return next;
        });
      }, 5000);
    } catch (err) {
      console.error("AI synthesis error:", err);
      const errMsg = err instanceof Error ? err.message : "Failed to generate draft with AI.";
      setErrorMessage(errMsg);
      setCardErrors((prev) => ({ ...prev, [req.id]: errMsg }));
    } finally {
      setIsGenerating((prev) => ({ ...prev, [req.id]: false }));
    }
  };

  // Insert library answer text into editor
  const handleInsertAnswer = (reqId: string, answerText: string, answerId?: string) => {
    const existing = sectionMap.get(reqId);
    setDraftEdits((prev) => {
      const current = prev[reqId]?.content ?? existing?.content ?? "";
      const updated = current.trim() ? `${current.trim()}\n\n${answerText}` : answerText;
      const currentUsed = prev[reqId]?.usedAnswerIds ?? existing?.usedAnswerIds ?? [];
      const newUsed =
        answerId && !currentUsed.includes(answerId) ? [...currentUsed, answerId] : currentUsed;
      return {
        ...prev,
        [reqId]: {
          content: updated,
          status: prev[reqId]?.status || existing?.status || "draft",
          usedAnswerIds: newUsed,
        },
      };
    });
  };

  // Insert completed SME response into editor
  const handleInsertSmeAnswer = (reqId: string, answerText: string, smeName?: string) => {
    const existing = sectionMap.get(reqId);
    const attribution = smeName
      ? `\n\n*(SME Contribution by ${smeName})*\n${answerText}`
      : `\n\n${answerText}`;
    setDraftEdits((prev) => {
      const current = prev[reqId]?.content ?? existing?.content ?? "";
      const updated = current.trim() ? `${current.trim()}${attribution}` : attribution.trim();
      return {
        ...prev,
        [reqId]: {
          content: updated,
          status: prev[reqId]?.status || existing?.status || "draft",
          usedAnswerIds: prev[reqId]?.usedAnswerIds ?? existing?.usedAnswerIds ?? [],
        },
      };
    });
  };

  // Toggle manually whether a matched answer is marked as used
  const handleToggleUsedAnswer = (reqId: string, answerId: string) => {
    const existing = sectionMap.get(reqId);
    setDraftEdits((prev) => {
      const currentContent = prev[reqId]?.content ?? existing?.content ?? "";
      const currentStatus = prev[reqId]?.status ?? existing?.status ?? "draft";
      const currentUsed = prev[reqId]?.usedAnswerIds ?? existing?.usedAnswerIds ?? [];
      const updatedUsed = currentUsed.includes(answerId)
        ? currentUsed.filter((id) => id !== answerId)
        : [...currentUsed, answerId];
      return {
        ...prev,
        [reqId]: {
          content: currentContent,
          status: currentStatus,
          usedAnswerIds: updatedUsed,
        },
      };
    });
  };

  // Execute Batch Auto-Draft
  const handleExecuteBatchAutoDraft = async () => {
    if (eligibleBatchCandidates.length === 0) return;
    setIsBatchAutoDrafting(true);
    setBatchProgress({ current: 0, total: eligibleBatchCandidates.length });

    try {
      for (let i = 0; i < eligibleBatchCandidates.length; i++) {
        const { requirement, topMatch } = eligibleBatchCandidates[i]!;
        setBatchProgress({ current: i + 1, total: eligibleBatchCandidates.length });

        const matches = requirementMatches[requirement.id] || [topMatch];
        let text = "";
        let used = [topMatch.answerId];

        try {
          const aiResult = await generateAiDraftSection({
            opportunityId: opportunity.id,
            requirementId: requirement.id,
            requirementTitle: requirement.title,
            requirementPrompt: requirement.prompt,
            requirementCategory: requirement.category,
            clientName: opportunity.client,
            opportunityTitle: opportunity.title,
            matchedAnswers: matches,
          });
          text = aiResult.text;
          used = aiResult.answersUsed || [topMatch.answerId];
        } catch {
          text = topMatch.fullBody || topMatch.snippet;
        }

        const existing = sectionMap.get(requirement.id);
        const reqTargetWords = requirement.targetWordCount || 350;

        await saveDraftSection(
          opportunity.id,
          {
            id: existing?.id,
            requirementId: requirement.id,
            title: requirement.title,
            content: text,
            status: "draft",
            matchedAnswerIds: matches.map((m) => m.answerId),
            usedAnswerIds: used,
            humanEdited: false,
            sectionRef: requirement.sectionRef,
            category: requirement.category,
            assignedTo: requirement.assignedTo,
            assignedToEmail: requirement.assignedToEmail,
            targetWordCount: reqTargetWords,
          },
          user ? { displayName: user.displayName, email: user.email } : undefined,
        );

        setDraftEdits((prev) => ({
          ...prev,
          [requirement.id]: {
            content: text,
            status: "draft",
            usedAnswerIds: used,
          },
        }));
      }

      setMatchSuccessMsg(
        `Batch auto-draft complete! Synthesized initial drafts for ${eligibleBatchCandidates.length} high-confidence requirements.`,
      );
      setIsBatchModalOpen(false);
    } catch (err) {
      console.error("Batch auto draft error:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to complete batch auto-drafting.",
      );
    } finally {
      setIsBatchAutoDrafting(false);
      setBatchProgress(null);
    }
  };

  // Helper to render an individual requirement draft card
  const renderRequirementCard = (req: Requirement) => {
    const section = sectionMap.get(req.id);
    const currentEdit = draftEdits[req.id] || {
      content: section?.content || "",
      status: section?.status || "draft",
      usedAnswerIds: section?.usedAnswerIds || [],
    };
    const currentUsedIds = currentEdit.usedAnswerIds ?? section?.usedAnswerIds ?? [];
    const words = countWords(currentEdit.content);
    const placeholders = countPlaceholders(currentEdit.content);
    const isSaving = Boolean(currentEdit.isSaving);
    const isSynthesizing = Boolean(isGenerating[req.id]);
    const matches = requirementMatches[req.id] || [];
    const isLoadingMatches = Boolean(loadingMatches[req.id]);

    // SME tasks linked to this requirement
    const reqSmeTasks = smeTasks.filter((t) => t.requirementId === req.id);
    const hasPendingSme = reqSmeTasks.some((t) => t.status === "open");
    const completedSme = reqSmeTasks.filter(
      (t) => t.status === "submitted" || t.status === "accepted",
    );

    // Word budget computation
    const parentSectionGroup = sectionGroups.find((g) =>
      g.requirements.some((r) => r.id === req.id),
    );
    const reqTargetWords =
      req.targetWordCount ||
      Math.round(
        (parentSectionGroup?.targetWordCount || 500) /
          Math.max(1, parentSectionGroup?.requirements.length || 1),
      );

    // Status Badge styling
    let statusBadge = "Unanswered";
    let statusBadgeColor = "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
    if (section) {
      if (section.status === "approved") {
        statusBadge = "Approved";
        statusBadgeColor =
          "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300";
      } else if (section.status === "review") {
        statusBadge = "In Review";
        statusBadgeColor =
          "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300";
      } else {
        statusBadge = "Draft";
        statusBadgeColor = "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300";
      }
    }

    const refPrefix = req.sectionRef ? `${req.sectionRef} ` : "";

    return (
      <ExpandableCard
        key={req.id}
        title={`${refPrefix}${req.title}`}
        badge={statusBadge}
        badgeColor={statusBadgeColor}
        summary={
          <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-500">
            <span className="capitalize font-medium">{req.category}</span>
            <span>•</span>
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {words} / {reqTargetWords}w
            </span>
            {req.assignedTo && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <User className="h-3 w-3 text-slate-400" />
                <span>{req.assignedTo}</span>
              </span>
            )}
            {hasPendingSme && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                <Clock className="h-2.5 w-2.5" />
                <span>SME Pending</span>
              </span>
            )}
            {completedSme.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                <CheckCircle2 className="h-2.5 w-2.5" />
                <span>
                  {completedSme.length} SME Response{completedSme.length > 1 ? "s" : ""}
                </span>
              </span>
            )}
            {placeholders > 0 && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
                {placeholders} Action Items
              </span>
            )}
            {matches.length > 0 && (
              <span className="text-[#2ba5b5] font-semibold text-[11px]">
                {matches.length} Match{matches.length > 1 ? "es" : ""}
              </span>
            )}
          </div>
        }
        isExpanded={expandedCardIds[req.id] ?? false}
        onToggle={() =>
          setExpandedCardIds((prev) => ({
            ...prev,
            [req.id]: !(prev[req.id] ?? false),
          }))
        }
        headerActions={
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setAskSmeReq(req)}
              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              title="Route technical question to Subject Matter Expert"
            >
              <HelpCircle className="h-3 w-3 text-indigo-500" />
              <span>Ask SME</span>
            </button>

            <button
              type="button"
              onClick={() => handleRunAiSynthesis(req)}
              disabled={isSynthesizing}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 disabled:opacity-50"
            >
              {isSynthesizing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Synthesizing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3" />
                  <span>Synthesize AI</span>
                </>
              )}
            </button>
          </div>
        }
        details={
          <div className="grid gap-6 lg:grid-cols-12 pt-2">
            {/* Left Pane: Draft Editor & Preview (8 cols) */}
            <div className="space-y-4 lg:col-span-8">
              {/* Card Level Error Alert */}
              {cardErrors[req.id] && (
                <div className="flex items-center justify-between rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800 border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900/50 dark:text-rose-300">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                    <span>{cardErrors[req.id]}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setCardErrors((prev) => {
                        const next = { ...prev };
                        delete next[req.id];
                        return next;
                      })
                    }
                    className="text-rose-600 hover:text-rose-800 font-bold"
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Card Level Success Alert */}
              {cardSuccess[req.id] && (
                <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900/50 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span>{cardSuccess[req.id]}</span>
                </div>
              )}

              {/* Synthesizing in-progress Banner */}
              {isSynthesizing && (
                <div className="flex items-center gap-2 rounded-lg bg-teal-50 p-2.5 text-xs text-teal-800 border border-teal-200 dark:bg-teal-950/40 dark:border-teal-900/50 dark:text-teal-300 animate-pulse">
                  <Loader2 className="h-4 w-4 animate-spin text-[#2ba5b5]" />
                  <span>
                    Synthesizing response narrative with Gemini & grounded library precedents...
                  </span>
                </div>
              )}

              {/* RFP Prompt Quote */}
              <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-700 border-l-2 border-[#2ba5b5] dark:bg-slate-900/60 dark:text-slate-300">
                <strong className="text-slate-900 dark:text-white">RFP Instructions:</strong>{" "}
                {req.prompt}
                {req.sourcePage && (
                  <span className="ml-1 text-slate-400">(p. {req.sourcePage})</span>
                )}
              </div>

              {/* Status Switcher Bar */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-slate-800 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 font-medium">Stage Status:</span>
                  <select
                    value={currentEdit.status}
                    onChange={(e) => {
                      const newStatus = e.target.value as "draft" | "review" | "approved";
                      setDraftEdits((prev) => ({
                        ...prev,
                        [req.id]: { ...currentEdit, status: newStatus },
                      }));
                    }}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    <option value="draft">Draft</option>
                    <option value="review">In Review</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>

                {parentSectionGroup && (
                  <span className="text-[11px] text-slate-400">
                    Section: <strong>{parentSectionGroup.title}</strong>
                  </span>
                )}
              </div>

              {/* Rich Markdown Editor & Live Preview */}
              <MarkdownEditorPreview
                content={currentEdit.content}
                onChange={(val) => {
                  setDraftEdits((prev) => ({
                    ...prev,
                    [req.id]: { ...currentEdit, content: val },
                  }));
                }}
                targetWordCount={reqTargetWords}
              />

              {/* Used Sources Audit Trail */}
              {currentUsedIds.length > 0 && (
                <div className="rounded-lg border border-teal-100 bg-teal-50/50 p-2.5 text-xs dark:border-teal-900/40 dark:bg-teal-950/20">
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    Used Precedents & Sources ({currentUsedIds.length}):
                  </span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {currentUsedIds.map((ansId) => {
                      const ans = matches.find((m) => m.answerId === ansId);
                      return (
                        <span
                          key={ansId}
                          className="inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-[10px] font-medium text-teal-800 border border-teal-200 dark:bg-slate-900 dark:border-teal-800 dark:text-teal-300"
                        >
                          <BookOpen className="h-2.5 w-2.5" />
                          <span>{ans ? ans.title : `Answer #${ansId}`}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bottom Actions */}
              <div className="flex items-center justify-between pt-1">
                <div>
                  {section && (
                    <button
                      type="button"
                      onClick={() => handleDeleteDraft(req)}
                      className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete Draft</span>
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleRunAiSynthesis(req)}
                    disabled={isSynthesizing}
                    className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 disabled:opacity-50"
                  >
                    {isSynthesizing ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Synthesizing...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>Synthesize AI</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSaveDraft(req)}
                    disabled={isSaving}
                    className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#238997] disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-3.5 w-3.5" />
                        <span>Save Draft</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Right Pane: Matched Library Answers & SME Drawer (4 cols) */}
            <div className="space-y-4 lg:col-span-4 border-l border-slate-100 pl-0 lg:pl-6 dark:border-slate-800">
              {/* SME Collaboration Section */}
              <div className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 dark:border-indigo-950 dark:bg-indigo-950/20">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-900 dark:text-indigo-200">
                    <User className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                    <span>SME Contributions</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setAskSmeReq(req)}
                    className="text-[11px] font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
                  >
                    + Ask SME
                  </button>
                </div>

                {reqSmeTasks.length === 0 ? (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    No SME inquiries routed yet for this requirement.
                  </p>
                ) : (
                  <div className="space-y-2 pt-1">
                    {reqSmeTasks.map((t) => (
                      <div
                        key={t.id}
                        className="rounded-lg bg-white p-2.5 text-xs border border-indigo-200 shadow-2xs dark:bg-slate-900 dark:border-indigo-900"
                      >
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {t.assigneeName}
                          </span>
                          <span
                            className={`rounded-full px-1.5 py-0.2 font-bold ${
                              t.status === "submitted" || t.status === "accepted"
                                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                                : "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                            }`}
                          >
                            {t.status.toUpperCase()}
                          </span>
                        </div>

                        {t.response ? (
                          <div className="mt-1.5">
                            <p className="rounded bg-slate-50 p-1.5 text-[11px] text-slate-700 italic border-l-2 border-indigo-400 dark:bg-slate-950 dark:text-slate-300">
                              {t.response}
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                handleInsertSmeAnswer(req.id, t.response!, t.assigneeName)
                              }
                              className="mt-1.5 btn-animate inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline dark:text-indigo-400"
                            >
                              <span>Insert SME Answer</span>
                              <ArrowRight className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                            Question sent: &ldquo;{t.question}&rdquo;
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Matched Answer Library Answers */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
                    <BookOpen className="h-4 w-4 text-[#2ba5b5]" />
                    <span>Matched Library Answers</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLoadMatchesForRequirement(req.id)}
                    disabled={isLoadingMatches}
                    className="text-[11px] font-semibold text-[#2ba5b5] hover:underline"
                  >
                    {isLoadingMatches ? "Searching..." : "Find Matches"}
                  </button>
                </div>

                {matches.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400 dark:border-slate-800">
                    <p>No answers matched yet.</p>
                    <button
                      type="button"
                      onClick={() => handleLoadMatchesForRequirement(req.id)}
                      className="mt-2 text-xs font-semibold text-[#2ba5b5] hover:underline"
                    >
                      Click to query library
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-[450px] overflow-y-auto pr-1">
                    {matches.map((ans) => (
                      <div
                        key={ans.answerId}
                        className="rounded-xl border border-slate-200 bg-white p-3 text-xs shadow-2xs dark:border-slate-800 dark:bg-slate-900/60"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-0.5 min-w-0">
                            <h4 className="font-semibold text-slate-900 dark:text-white line-clamp-1">
                              {ans.title}
                            </h4>
                            {ans.wonBidCount && ans.wonBidCount > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.2 text-[9px] font-bold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                🏆 Won ({ans.wonBidCount})
                              </span>
                            ) : null}
                          </div>
                          <span className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-[#2ba5b5] dark:bg-teal-950/40">
                            {Math.round(ans.matchScore * 100)}% Match
                          </span>
                        </div>

                        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 line-clamp-3">
                          {ans.snippet}
                        </p>

                        <div className="mt-2 flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={currentUsedIds.includes(ans.answerId)}
                              onChange={() => handleToggleUsedAnswer(req.id, ans.answerId)}
                              className="rounded text-[#2ba5b5] focus:ring-[#2ba5b5]"
                            />
                            <span>Used in draft</span>
                          </label>

                          <button
                            type="button"
                            onClick={() =>
                              handleInsertAnswer(req.id, ans.fullBody || ans.snippet, ans.answerId)
                            }
                            className="btn-animate inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ba5b5] hover:underline"
                          >
                            <span>Insert</span>
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        }
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Status Strip */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-4 dark:border-slate-800">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <PenTool className="h-5 w-5 text-[#2ba5b5]" />
            <span>Proposal Drafting Board</span>
          </h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Author and synthesize response sections grounded in verified Answer Library precedents
            and SME inputs.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onOpenHarvest && (
            <button
              type="button"
              onClick={onOpenHarvest}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
              title="Harvest new reusable responses from this proposal into the Answer Library"
            >
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span>Harvest to Library</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsBatchModalOpen(true)}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300"
            title="Auto-draft all unanswered requirements with high-confidence library matches"
          >
            <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
            <span>Auto-Draft Matches</span>
          </button>

          <button
            type="button"
            onClick={handleMatchAll}
            disabled={isMatchingAll}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3.5 py-2 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 disabled:opacity-50"
          >
            {isMatchingAll ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Matching with Library...</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" />
                <span>Match All with Answer Library</span>
              </>
            )}
          </button>
        </div>
      </div>

      {matchSuccessMsg && (
        <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-4 py-2.5 text-xs font-medium text-emerald-800 border border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-900 dark:text-emerald-200">
          <span>{matchSuccessMsg}</span>
          <button
            type="button"
            onClick={() => setMatchSuccessMsg(null)}
            className="text-emerald-600 hover:text-emerald-800 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-2.5 text-xs font-medium text-red-800 border border-red-200 dark:bg-red-950/50 dark:border-red-900 dark:text-red-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
            <span>{errorMessage}</span>
          </div>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="text-red-600 hover:text-red-800 dark:text-red-400 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* Progress & Budget Metrics Cards */}
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="glass rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800/80">
          <span className="text-[11px] font-semibold uppercase text-slate-500">Progress</span>
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-xl font-bold text-slate-900 dark:text-white">
              {approvedCount} / {totalSections}
            </span>
            <span className="text-xs font-bold text-emerald-600">
              {totalSections > 0 ? Math.round((approvedCount / totalSections) * 100) : 0}% Approved
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{
                width: `${totalSections > 0 ? (approvedCount / totalSections) * 100 : 0}%`,
              }}
            />
          </div>
        </div>

        <div className="glass rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800/80">
          <span className="text-[11px] font-semibold uppercase text-slate-500">
            Total Word Count
          </span>
          <p className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            {totalWords.toLocaleString()}{" "}
            {totalTargetWords > 0 && (
              <span className="text-xs font-normal text-slate-400">
                / {totalTargetWords} planned
              </span>
            )}
          </p>
          <p className="mt-1 text-[11px] text-slate-400">
            ~{Math.round(totalWords / 450)} pages (est. 450 w/p)
          </p>
        </div>

        <div className="glass rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800/80">
          <span className="text-[11px] font-semibold uppercase text-slate-500">In Review</span>
          <p className="mt-1 text-xl font-bold text-purple-600 dark:text-purple-400">
            {inReviewCount} <span className="text-xs font-normal text-slate-400">sections</span>
          </p>
          <p className="mt-1 text-[11px] text-slate-400">Pending team sign-off</p>
        </div>

        <div className="glass rounded-xl p-3.5 border border-slate-200/80 dark:border-slate-800/80">
          <span className="text-[11px] font-semibold uppercase text-slate-500">
            Drafts in Progress
          </span>
          <p className="mt-1 text-xl font-bold text-amber-600 dark:text-amber-400">
            {draftedCount} <span className="text-xs font-normal text-slate-400">sections</span>
          </p>
          <p className="mt-1 text-[11px] text-slate-400">Active working drafts</p>
        </div>
      </div>

      {/* Filter, View Switcher & Search Bar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        {/* View Mode Toggle + Status Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode: Sections vs Flat */}
          <div className="flex items-center rounded-lg bg-slate-200/70 p-1 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setViewMode("sections")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                viewMode === "sections"
                  ? "bg-white text-[#2ba5b5] shadow-xs dark:bg-slate-700 dark:text-teal-300"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <Layers className="h-3.5 w-3.5" />
              <span>By Section</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("flat")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                viewMode === "flat"
                  ? "bg-white text-[#2ba5b5] shadow-xs dark:bg-slate-700 dark:text-teal-300"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-400"
              }`}
            >
              <List className="h-3.5 w-3.5" />
              <span>All Requirements</span>
            </button>
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            {[
              { id: "all", label: "All Status" },
              { id: "unanswered", label: "Unanswered" },
              { id: "draft", label: "Draft" },
              { id: "review", label: "In Review" },
              { id: "approved", label: "Approved" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setStatusFilter(tab.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                  statusFilter === tab.id
                    ? "bg-white text-[#2ba5b5] shadow-xs dark:bg-slate-700 dark:text-teal-300"
                    : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Quick Filter: Assigned to Me | Gaps | Actions */}
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setSpecialFilter(specialFilter === "mine" ? "all" : "mine")}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-all ${
                specialFilter === "mine"
                  ? "bg-[#2ba5b5] text-white shadow-xs"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
              }`}
            >
              <User className="h-3 w-3" />
              <span>Assigned to Me</span>
            </button>

            <button
              type="button"
              onClick={() => setSpecialFilter(specialFilter === "gaps" ? "all" : "gaps")}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-all ${
                specialFilter === "gaps"
                  ? "bg-amber-600 text-white shadow-xs"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
              }`}
            >
              <AlertTriangle className="h-3 w-3" />
              <span>Knowledge Gaps</span>
            </button>

            <button
              type="button"
              onClick={() => setSpecialFilter(specialFilter === "actions" ? "all" : "actions")}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-all ${
                specialFilter === "actions"
                  ? "bg-rose-600 text-white shadow-xs"
                  : "text-slate-600 hover:text-slate-900 dark:text-slate-300"
              }`}
            >
              <span>Action Items</span>
            </button>
          </div>
        </div>

        {/* Search Input */}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search requirements or drafts..."
            className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>
      </div>

      {/* Overwrite Confirmation Dialog Modal */}
      <AnimatePresence>
        {confirmOverwriteReqId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmOverwriteReqId(null)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              className="relative w-full max-w-md rounded-2xl border border-amber-300 bg-white p-6 shadow-2xl dark:border-amber-900/50 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    Overwrite manual draft with AI narrative?
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    This requirement section already contains text. Running AI synthesis will
                    replace your current content with a new synthesized draft.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setConfirmOverwriteReqId(null)}
                  className="rounded-md border border-amber-300 bg-white px-3 py-1 font-semibold text-amber-800 hover:bg-amber-100 dark:bg-slate-900 dark:border-amber-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const req = requirements.find((r) => r.id === confirmOverwriteReqId);
                    if (req) handleRunAiSynthesis(req, true);
                  }}
                  className="rounded-md bg-amber-600 px-3 py-1 font-semibold text-white hover:bg-amber-700"
                >
                  Yes, Overwrite & Synthesize
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog Modal */}
      <AnimatePresence>
        {confirmDeleteReq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConfirmDeleteReq(null)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              className="relative w-full max-w-md rounded-2xl border border-rose-300 bg-white p-6 shadow-2xl dark:border-rose-900/50 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-950/60 dark:text-rose-400">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    Delete draft response for "{confirmDeleteReq.title}"?
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    This action will delete this draft response and revert the requirement to
                    unanswered.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteReq(null)}
                  className="rounded-md border border-rose-300 bg-white px-3 py-1 font-semibold text-rose-800 hover:bg-rose-100 dark:bg-slate-900 dark:border-rose-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => executeDeleteDraft(confirmDeleteReq)}
                  className="rounded-md bg-rose-600 px-3 py-1 font-semibold text-white hover:bg-rose-700"
                >
                  Yes, Delete Draft
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Main Drafting Views */}
      {filteredRequirements.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
          <PenTool className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
            No requirements match your current filter.
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Try adjusting your search query, status filter, or special filter.
          </p>
        </div>
      ) : viewMode === "sections" ? (
        /* Outline Mode: Grouped by Proposal Sections */
        <div className="space-y-6">
          {sectionGroups
            .filter((group) => {
              // Only display section if at least one requirement matches the filters
              return group.requirements.some((r) =>
                filteredRequirements.some((fr) => fr.id === r.id),
              );
            })
            .map((group) => {
              const groupReqs = group.requirements.filter((r) =>
                filteredRequirements.some((fr) => fr.id === r.id),
              );
              const isExpanded = expandedSections[group.id] ?? true;

              // Aggregate actual words in this section
              const secActualWords = groupReqs.reduce((sum, r) => {
                const draft = draftEdits[r.id]?.content ?? sectionMap.get(r.id)?.content ?? "";
                return sum + countWords(draft);
              }, 0);
              const secTargetWords = group.targetWordCount || groupReqs.length * 350;
              const secRatio = secTargetWords > 0 ? secActualWords / secTargetWords : 0;

              return (
                <div
                  key={group.id}
                  className="rounded-2xl border border-slate-200/90 bg-white dark:border-slate-800 dark:bg-slate-900 shadow-xs overflow-hidden"
                >
                  {/* Section Group Header */}
                  <div
                    onClick={() =>
                      setExpandedSections((prev) => ({
                        ...prev,
                        [group.id]: !isExpanded,
                      }))
                    }
                    className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between bg-slate-50/70 hover:bg-slate-100/70 dark:bg-slate-800/40 dark:hover:bg-slate-800/60 cursor-pointer transition-colors border-b border-slate-200/60 dark:border-slate-800/60"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        aria-label={isExpanded ? "Collapse section" : "Expand section"}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="rounded bg-teal-50 px-2 py-0.5 text-xs font-bold text-[#2ba5b5] dark:bg-teal-950/60">
                            {group.sectionRef}
                          </span>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                            {group.title}
                          </h3>
                          <span className="capitalize rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                            {group.category}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          {group.assignedTo && (
                            <span className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300">
                              <User className="h-3 w-3 text-slate-400" />
                              <span>Lead: {group.assignedTo}</span>
                            </span>
                          )}
                          {group.reviewerName && (
                            <span className="inline-flex items-center gap-1 font-medium text-indigo-600 dark:text-indigo-300">
                              <HelpCircle className="h-3 w-3" />
                              <span>SME: {group.reviewerName}</span>
                            </span>
                          )}
                          <span>•</span>
                          <span>
                            {groupReqs.length} requirement{groupReqs.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Section Word Budget Meter */}
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                          {secActualWords} / {secTargetWords} words
                        </span>
                        <span className="ml-1 text-[11px] text-slate-400">
                          ({Math.round(secRatio * 100)}%)
                        </span>
                        <div className="mt-1 h-1.5 w-32 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                          <div
                            className={`h-full transition-all duration-300 ${
                              secRatio > 1.1
                                ? "bg-rose-500"
                                : secRatio >= 0.7
                                  ? "bg-emerald-500"
                                  : "bg-[#2ba5b5]"
                            }`}
                            style={{ width: `${Math.min(100, secRatio * 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section Requirements Cards */}
                  {isExpanded && (
                    <div className="p-4 space-y-4">
                      {groupReqs.map((req) => renderRequirementCard(req))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ) : (
        /* Flat View Mode */
        <div className="space-y-4">
          {filteredRequirements.map((req) => renderRequirementCard(req))}
        </div>
      )}

      {/* Ask SME Modal */}
      {askSmeReq && (
        <AskSmeModal
          isOpen={Boolean(askSmeReq)}
          onClose={() => setAskSmeReq(null)}
          opportunity={opportunity}
          requirement={askSmeReq}
        />
      )}

      {/* Batch Auto-Draft Modal */}
      <BatchAutoDraftModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        eligibleCandidates={eligibleBatchCandidates}
        onExecute={handleExecuteBatchAutoDraft}
        isProcessing={isBatchAutoDrafting}
        progress={batchProgress}
      />
    </div>
  );
};
